// SPDX-License-Identifier: AGPL-3.0-or-later

export const CHANNEL_TITLE_ANIMATIONS = ['none', 'glow', 'pulse', 'shimmer', 'float'] as const;
export type ChannelTitleAnimation = (typeof CHANNEL_TITLE_ANIMATIONS)[number];
export const CHANNEL_LAYOUTS = ['default', 'feed', 'marketplace', 'music', 'raffle'] as const;
export type ChannelLayout = (typeof CHANNEL_LAYOUTS)[number];

export interface ChannelAppearance {
	color: number;
	animation: ChannelTitleAnimation;
	layout?: ChannelLayout;
}

const APPEARANCE_TAG =
	/\s*<!--coret-channel:color=#([0-9a-fA-F]{6});animation=(none|glow|pulse|shimmer|float)(?:;layout=(default|feed|marketplace|music|raffle))?--?>\s*$/;

export function parseChannelAppearance(topic: string | null | undefined): ChannelAppearance {
	const match = topic?.match(APPEARANCE_TAG);
	if (!match) return {color: 0, animation: 'none', layout: 'default'};
	return {
		color: Number.parseInt(match[1] ?? '0', 16) >>> 0,
		animation: (match[2] ?? 'none') as ChannelTitleAnimation,
		layout: (match[3] ?? 'default') as ChannelLayout,
	};
}

export function stripChannelAppearance(topic: string | null | undefined): string {
	return (topic ?? '').replace(APPEARANCE_TAG, '').trimEnd();
}

export function serializeChannelAppearance(topic: string, appearance: ChannelAppearance): string {
	const layout = appearance.layout ?? parseChannelAppearance(topic).layout ?? 'default';
	const cleanTopic = stripChannelAppearance(topic);
	if (appearance.color === 0 && appearance.animation === 'none' && layout === 'default') return cleanTopic;
	const color = Math.max(0, Math.min(0xffffff, appearance.color >>> 0))
		.toString(16)
		.padStart(6, '0');
	const tag = `<!--coret-channel:color=#${color};animation=${appearance.animation};layout=${layout}-->`;
	return cleanTopic ? `${cleanTopic}\n${tag}` : tag;
}
